import React from "react";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/brand";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { breadcrumbLd } from "../components/Seo";
import { BadgeCheck, HandHeart, ShieldCheck, Users, Award, Phone } from "lucide-react";

export default function AboutUs() {
  return (
    <div data-testid="about-us-page" className="min-h-screen bg-white overflow-x-hidden">
      <Seo
        title="About Us - Family-Owned Since 1959"
        description="Meet Dead Bug Edwards - three generations of family-owned termite and pest control across South Florida. Learn our story, our values and our 65-year commitment to honest service."
        canonical="/about-us"
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "About Us", path: "/about-us" },
        ])}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-[120px] md:pt-[160px] pb-16 sm:pb-20 bg-gradient-to-b from-[color:var(--dbe-cream)] to-white overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-[color:var(--dbe-red)]/5 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-[color:var(--dbe-gold)]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 text-center">
          <p className="text-[color:var(--dbe-red)] text-xs tracking-[0.35em] uppercase font-bold">About Us</p>
          <h1
            data-testid="about-page-headline"
            className="mt-4 font-bebas leading-[0.95] text-[clamp(2.75rem,7vw,6rem)] text-neutral-900"
          >
            Family owned.<br />
            <span className="text-[color:var(--dbe-red)]">Locally trusted since {BRAND.since}.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-neutral-700 leading-relaxed max-w-3xl mx-auto">
            Dead Bug Edwards is your premier one-stop-shop for all your termite and pest control
            needs across South Florida. For over 65 years, we've earned the trust of homeowners,
            business owners, boat owners and property managers - one honest, professional service
            call at a time.
          </p>
        </div>
      </section>

      {/* Story + portrait */}
      <section className="relative py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-3 order-2 lg:order-1">
            <p className="text-[color:var(--dbe-red)] text-xs tracking-[0.35em] uppercase font-bold">Our Story</p>
            <h2 className="mt-4 font-bebas text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-neutral-900">
              A promise Bob Edwards<br />
              <span className="text-[color:var(--dbe-red)]">made in 1959.</span>
            </h2>
            <div className="mt-6 space-y-5 text-neutral-700 text-base sm:text-lg leading-relaxed">
              <p>
                When Bob Edwards started Dead Bug Edwards over 65 years ago, he made a simple
                promise: give every customer the kind of service they'd tell their friends and
                family about. That promise built this company - and it still runs every truck we
                send out today.
              </p>
              <p>
                Three generations of South Florida families now call us for the same job Bob was
                doing back then. Grandparents introduced their kids to us; those kids introduced
                their kids. Because when you treat people right, they come back - and they bring
                their neighbors.
              </p>
              <p>
                We are never too busy to say <em className="not-italic font-semibold text-[color:var(--dbe-red)]">thank you for your business</em>.
                And we're proud to still answer our phone the same way Bob did: with a real human,
                a real solution, and a real guarantee.
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/#quote"
                data-testid="about-cta-quote"
                className="inline-flex items-center justify-center gap-2 bg-[color:var(--dbe-red)] hover:bg-[color:var(--dbe-red-deep)] text-white px-6 py-3.5 rounded-full font-bold uppercase tracking-wider text-xs sm:text-sm shadow-[0_15px_35px_-15px_rgba(227,30,36,0.55)]"
              >
                Get a Free Estimate <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
              <a
                href={`tel:${BRAND.phoneTel}`}
                data-testid="about-cta-call"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-neutral-900/10 hover:border-[color:var(--dbe-red)]/40 text-neutral-900 px-6 py-3.5 rounded-full font-bebas text-xl sm:text-2xl tracking-wide"
              >
                <Phone className="w-5 h-5 text-[color:var(--dbe-red)]" /> {BRAND.phoneDisplay}
              </a>
            </div>
          </div>

          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="flex flex-col items-center lg:items-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-[color:var(--dbe-gold)]/30 blur-3xl scale-110" />
                <img
                  src={BRAND.ownerPortrait}
                  alt="Bruce Edwards - Dead Bug Edwards"
                  data-testid="about-portrait"
                  className="relative w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 lg:w-full lg:h-[420px] lg:max-w-sm mx-auto rounded-full lg:rounded-[36px] object-cover ring-4 ring-white shadow-[0_40px_80px_-25px_rgba(15,15,25,0.35)]"
                  style={{ objectPosition: "center 25%" }}
                />
                <img
                  src={BRAND.badge65}
                  alt="Over 65 Years of Service"
                  className="absolute -bottom-6 -right-4 w-24 sm:w-32 md:w-40 rotate-[-10deg] drop-shadow-[0_20px_35px_rgba(0,0,0,0.35)]"
                />
              </div>
              <div className="mt-8 text-center">
                <div className="font-bebas text-3xl sm:text-4xl text-[color:var(--dbe-red)] tracking-wide leading-none">
                  Bruce Edwards
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.25em] text-neutral-500 font-semibold">
                  Owner
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative bg-[color:var(--dbe-cream)] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-[color:var(--dbe-red)] text-xs tracking-[0.35em] uppercase font-bold">What We Stand For</p>
            <h2 className="mt-4 font-bebas text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-neutral-900">
              Old-school service.<br />
              <span className="text-[color:var(--dbe-red)]">Modern-day science.</span>
            </h2>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              {
                icon: <Users className="w-6 h-6" />,
                title: "Family Owned",
                body: "Three generations of the same family have kept this company running - and personally answer the phone.",
              },
              {
                icon: <ShieldCheck className="w-6 h-6" />,
                title: "Licensed & Insured",
                body: "Every technician is certified, background-checked and fully insured before stepping on your property.",
              },
              {
                icon: <BadgeCheck className="w-6 h-6" />,
                title: "Locally Rooted",
                body: "Based in Plantation, FL - we know South Florida pests because we've been beating them here since 1959.",
              },
              {
                icon: <HandHeart className="w-6 h-6" />,
                title: "Guaranteed",
                body: "If pests come back between visits, so do we. Free. It's the same promise Bob made 65 years ago.",
              },
            ].map((v) => (
              <div
                key={v.title}
                data-testid={`about-value-${v.title.toLowerCase().replace(/\s/g, "-")}`}
                className="bg-white rounded-2xl p-6 sm:p-7 border border-black/5 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.25)]"
              >
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[color:var(--dbe-red)]/10 text-[color:var(--dbe-red)] flex items-center justify-center">
                  {v.icon}
                </div>
                <h3 className="mt-5 font-bebas text-xl sm:text-2xl tracking-wide">{v.title}</h3>
                <p className="mt-2 text-sm sm:text-base text-neutral-600 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 grid grid-cols-3 gap-5 text-center">
          {[
            { t: "65+", s: "Years serving South FL" },
            { t: "3", s: "Generations trusted" },
            { t: "100%", s: "Satisfaction focus" },
          ].map((b) => (
            <div key={b.s}>
              <div className="font-bebas text-5xl sm:text-6xl md:text-7xl text-[color:var(--dbe-red)] leading-none">
                {b.t}
              </div>
              <div className="mt-3 text-xs sm:text-sm text-neutral-700 uppercase tracking-wider">{b.s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[color:var(--dbe-red)] text-white py-14 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "repeating-linear-gradient(-45deg, rgba(255,255,255,0.25) 0 2px, transparent 2px 18px)",
        }} />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <Award className="mx-auto w-10 h-10 text-white/80" />
          <h2 className="mt-4 font-bebas text-4xl sm:text-5xl md:text-6xl leading-[1.02]">
            Ready to meet the family?
          </h2>
          <p className="mt-4 text-white/90 max-w-2xl mx-auto text-base sm:text-lg">
            Whether it's your first call or your fiftieth, you get the same treatment - honest,
            fast and guaranteed.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="about-final-call"
              className="inline-flex items-center justify-center gap-3 bg-white text-[color:var(--dbe-red)] px-7 py-4 rounded-full font-bebas text-xl sm:text-2xl tracking-wide"
            >
              <Phone className="w-5 h-5" /> {BRAND.phoneDisplay}
            </a>
            <Link
              to="/#quote"
              data-testid="about-final-quote"
              className="inline-flex items-center justify-center gap-2 bg-black/30 hover:bg-black/50 border border-white/40 text-white px-7 py-4 rounded-full font-bold uppercase tracking-wider text-xs sm:text-sm"
            >
              Free Estimate <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
